/**
 * HeightZoneTool.js
 *
 * Utility functions for drawing and managing height zones in Step 4 of the wizard.
 * Height zones are polygonal areas at different elevations (-200cm to +200cm).
 *
 * Based on PolygonTool.js patterns, with height-specific functionality added.
 */

import { Polygon } from 'fabric';
import {
  calculateDistance,
  isNearPoint,
  distanceToLineSegment,
  isPointInPolygon,
  addVertex,
  removeLastVertex,
  calculatePolygonArea,
  calculatePolygonCentroid,
  calculatePolygonPerimeter,
} from './PolygonTool';
import { GARDEN_OBJECT_TYPES, createGardenObject } from '../../../constants/gardenObjectTypes';

// Re-export commonly used PolygonTool functions
export {
  calculateDistance,
  isNearPoint,
  distanceToLineSegment,
  isPointInPolygon,
  removeLastVertex,
  calculatePolygonArea,
  calculatePolygonCentroid,
  calculatePolygonPerimeter,
};

// Zonen-Art (#625, Revision 2026-07-15): 'plateau' = ebene Fläche mit Höhe,
// 'slope' = Böschung ohne Höhe (interpoliert im 3D aus den Nachbar-Ebenen,
// s. scene3d/terrain.js). Eine Zone ohne kind gilt als Ebene (Bestand).
/** Neutrale Farbe für Böschungsflächen (keine Höhen-Farbkodierung). */
export const SLOPE_COLOR = '#9CA3AF'; // Gray-400

/** True, wenn die Zone eine Böschung ist. */
export function isSlopeZone(zone) {
  return zone?.kind === 'slope';
}

/** Gestrichelte Kontur für Böschungsflächen (2D-Darstellung v1). */
export function zoneStrokeDashArray(kind) {
  return kind === 'slope' ? [8, 5] : null;
}

/**
 * Get color for height zone based on elevation
 * Based on Design-Specs.md color coding system
 *
 * @param {number} height - Height in centimeters
 * @returns {string} - Hex color code
 */
export function getHeightColor(height) {
  if (height >= 80) return '#FFA500';   // Orange: +80cm and above
  if (height >= 50) return '#FFFF00';   // Yellow: +50cm to +79cm
  if (height >= 10) return '#90EE90';   // Light Green: +10cm to +49cm
  if (height >= -10) return '#90EE90';  // Light Green: -10cm to +9cm (ground level)
  if (height >= -30) return '#87CEEB';  // Light Blue: -30cm to -11cm
  return '#87CEEB';                      // Light Blue: Below -30cm
}

/**
 * Add vertex to height zone polygon
 * Wrapper around PolygonTool's addVertex with identical logic
 *
 * @param {Array<{x: number, y: number}>} vertices - Existing vertices
 * @param {{x: number, y: number}} point - New point to add
 * @param {boolean} ctrlPressed - Whether Ctrl key is pressed (disables snap)
 * @param {number} gridSize - Grid size in pixels for snapping
 * @param {boolean} snapEnabled - Whether snap-to-grid is enabled
 * @returns {{vertices: Array, shouldClose: boolean, isDuplicate: boolean}}
 */
export function addHeightZoneVertex(vertices, point, ctrlPressed, gridSize, snapEnabled) {
  return addVertex(vertices, point, ctrlPressed, gridSize, snapEnabled);
}

/**
 * Create Fabric.js Polygon for a height zone.
 *
 * Plateaus (`kind` omitted or 'plateau') are color-coded by height; slopes
 * (`kind: 'slope'`) use a neutral gray with a dashed contour and carry no
 * height (#625, Modell-Revision 2026-07-15).
 *
 * @param {Array<{x: number, y: number}>} vertices - Polygon vertices
 * @param {number} height - Height in centimeters (plateau only; ignored for slope)
 * @param {Object} options - { id, label, kind, selectable, evented, createdAt }
 * @returns {Polygon} - Fabric.js Polygon object
 */
export function createHeightZone(vertices, height, options = {}) {
  const isSlope = options.kind === 'slope';
  const color = isSlope ? SLOPE_COLOR : getHeightColor(height);

  // Generate UUID (fallback for environments without crypto.randomUUID)
  const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback: simple UUID v4 generator
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const zone = new Polygon(vertices, {
    selectable: options.selectable !== undefined ? options.selectable : false,
    evented: options.evented !== undefined ? options.evented : false,
    hasControls: false,
    hasBorders: false,
    originX: 'left',
    originY: 'top',
  });

  // Apply garden object type system
  createGardenObject(GARDEN_OBJECT_TYPES.HEIGHT_ZONE, zone, {
    fill: `${color}33`,  // 20% opacity (33 in hex = ~20%)
    stroke: color,
    strokeWidth: 2,
    // Böschungsflächen bekommen eine gestrichelte Kontur
    strokeDashArray: zoneStrokeDashArray(options.kind),
    data: {
      shapeType: 'height-zone',
      kind: isSlope ? 'slope' : 'plateau',
      ...(isSlope ? {} : { height }),
      color,
      id: options.id || generateId(),
      label: options.label || '',
      isClosed: true,
      createdAt: options.createdAt || new Date().toISOString(),
    },
    heightDifference: isSlope ? undefined : height,
    label: options.label || '',
  });

  return zone;
}

/**
 * Validate height zone for required fields and constraints
 *
 * @param {Array<{x: number, y: number}>} vertices - Polygon vertices
 * @param {number} height - Height in centimeters
 * @returns {{isValid: boolean, errors: Object}} - Validation result
 */
export function validateHeightZone(vertices, height) {
  const errors = {};

  // Validate vertices
  if (!vertices || !Array.isArray(vertices)) {
    errors.vertices = 'Vertices must be an array';
  } else if (vertices.length < 3) {
    errors.vertices = 'Mindestens 3 Punkte erforderlich';
  }

  // Validate height
  if (height === null || height === undefined) {
    errors.height = 'Höhe ist erforderlich';
  } else if (typeof height !== 'number') {
    errors.height = 'Höhe muss eine Zahl sein';
  } else if (height < -200 || height > 200) {
    errors.height = 'Höhe muss zwischen -200 und +200 cm liegen';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Get comprehensive height zone properties
 *
 * @param {Polygon} zone - Fabric.js Polygon object
 * @param {number} pixelsPerCm - Conversion factor (default: 1px = 1cm)
 * @returns {Object} - Zone properties
 */
export function getHeightZoneProperties(zone, pixelsPerCm = 1) {
  const vertices = zone.points.map(p => ({ x: p.x, y: p.y }));
  const areaPx = calculatePolygonArea(vertices);
  const perimeterPx = calculatePolygonPerimeter(vertices);
  const centroid = calculatePolygonCentroid(vertices);

  // Convert from pixels to square meters and meters
  const areaSqm = (areaPx / (pixelsPerCm * pixelsPerCm)) / 10000; // px² → cm² → m²
  const perimeterM = (perimeterPx / pixelsPerCm) / 100; // px → cm → m

  return {
    vertices,
    vertexCount: vertices.length,
    height: zone.data?.height || 0,
    color: zone.data?.color || '#90EE90',
    areaSqm: parseFloat(areaSqm.toFixed(2)),
    perimeterM: parseFloat(perimeterM.toFixed(2)),
    centroid,
    id: zone.data?.id,
    label: zone.data?.label || '',
    createdAt: zone.data?.createdAt,
  };
}

/**
 * Update height zone appearance (color, fill) based on new height
 *
 * @param {Polygon} zone - Fabric.js Polygon object
 * @param {number} newHeight - New height in centimeters
 * @returns {void}
 */
export function updateHeightZoneAppearance(zone, newHeight) {
  const color = getHeightColor(newHeight);

  zone.set({
    fill: `${color}33`,  // 20% opacity
    stroke: color,
  });

  // Update metadata
  if (zone.data) {
    zone.data.height = newHeight;
    zone.data.color = color;
  }
}

/**
 * Format height value for display
 *
 * @param {number} height - Height in centimeters
 * @returns {string} - Formatted height (e.g., "+50 cm", "-30 cm", "0 cm")
 */
export function formatHeightLabel(height) {
  if (height > 0) {
    return `+${height} cm`;
  }
  return `${height} cm`;
}

/**
 * Format zone label (#625, Modell-Revision). Plateaus show their height,
 * slopes show „Böschung" (no height). Die echte Verlaufsoptik gibt es nur in 3D.
 *
 * @param {{height?: number, kind?: string}} zone
 * @returns {string} - e.g. "+50 cm" or "Böschung"
 */
export function formatZoneLabel(zone) {
  if (zone.kind === 'slope') return 'Böschung';
  return formatHeightLabel(zone.height);
}

/**
 * Check if two height zones overlap (simple bounding box check)
 * Note: This is a simple approximation, not precise polygon intersection
 *
 * @param {Array<{x: number, y: number}>} vertices1 - First zone vertices
 * @param {Array<{x: number, y: number}>} vertices2 - Second zone vertices
 * @returns {boolean} - True if bounding boxes overlap
 */
export function doZonesOverlap(vertices1, vertices2) {
  // Get bounding boxes
  const bbox1 = getBoundingBox(vertices1);
  const bbox2 = getBoundingBox(vertices2);

  // Check if bounding boxes overlap
  return !(
    bbox1.right < bbox2.left ||
    bbox1.left > bbox2.right ||
    bbox1.bottom < bbox2.top ||
    bbox1.top > bbox2.bottom
  );
}

/**
 * Get bounding box for a set of vertices
 *
 * @param {Array<{x: number, y: number}>} vertices - Polygon vertices
 * @returns {{left: number, right: number, top: number, bottom: number}}
 */
function getBoundingBox(vertices) {
  const xs = vertices.map(v => v.x);
  const ys = vertices.map(v => v.y);

  return {
    left: Math.min(...xs),
    right: Math.max(...xs),
    top: Math.min(...ys),
    bottom: Math.max(...ys),
  };
}
